import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useEditMetric } from 'src/data/impact/mutation/impact-mutation';
import { useSnackbar } from '../snackbar';
import { useGetUser } from 'src/hooks/useGetUser';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Tooltip } from '@mui/material';

const TitleMetric = ({ id, title, isVolet }: { id: string; title: string, isVolet: string }) => {
  const user = useGetUser();

  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState(title || '');
  const quillRef = useRef<ReactQuill>(null);
  const { enqueueSnackbar } = useSnackbar();
  const editMetric = useEditMetric();
  const pasteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to strip HTML tags
  const stripHtmlTags = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const colorPalette = [
    '#000000', // Black
    '#FFFFFF', // White
    '#FF0000', // Red
    '#00AA55', // Green
    '#0099F2', // Blue
    '#FFFF00', // Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
  ];

  const icons = Quill.import('ui/icons');

  // Dynamically create icons for each color
  colorPalette.forEach((color) => {
    icons[color] = `<svg viewBox="0 0 18 18" width="18" height="18">
    <circle cx="9" cy="9" r="7" fill="${color}" stroke="black" stroke-width="1"/>
  </svg>`;
  });

  const formats = ['bold', 'italic', 'color'];

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [['bold', 'italic', ...colorPalette]],
        handlers: {
          color: function (this: any, value: string) {
            const quill = this.quill;
            const range = quill.getSelection();

            if (range) {
              quill.format('color', value);
            }
          },
          ...colorPalette.reduce((handlers, color) => {
            handlers[color] = function (this: any) {
              const quill = this.quill;
              const range = quill.getSelection();

              if (range) {
                quill.format('color', color);
              }
            };
            return handlers;
          }, {} as Record<string, (this: any) => void>),
        },
      },
    }),
    []
  );

  useEffect(() => {
    if (title) {
      setData(title || '');
    }
  }, [title]);

  useEffect(() => {
    if (isEditing && quillRef.current) {
      const quillEditor = quillRef.current.getEditor().root;
      
      const handlePaste = (e: ClipboardEvent) => {
        // Clear any existing timeout
        if (pasteTimeoutRef.current) {
          clearTimeout(pasteTimeoutRef.current);
        }
        
        // Prevent the default blur behavior
        e.stopPropagation();
      };

      const handleBlur = (e: FocusEvent) => {
        // Add a delay to check if the blur is due to a paste operation
        pasteTimeoutRef.current = setTimeout(() => {
          setIsEditing(false);
          editMetric.mutate(
            {
              [isVolet ? "titleV" : "title"]: data.toString(),
              id,
            },
            {
              onSuccess: (res) => {
                enqueueSnackbar('mis à jour avec succès.');
              },
              onError: (err) => {
                enqueueSnackbar("quelque chose s'est mal passé", {
                  variant: 'error',
                });
              },
            }
          );
        }, 200);
      };

      quillEditor.addEventListener('paste', handlePaste);
      quillEditor.addEventListener('blur', handleBlur);

      return () => {
        quillEditor.removeEventListener('paste', handlePaste);
        quillEditor.removeEventListener('blur', handleBlur);
        
        // Clear any lingering timeout
        if (pasteTimeoutRef.current) {
          clearTimeout(pasteTimeoutRef.current);
        }
      };
    }
  }, [isEditing, data, id, isVolet, editMetric, enqueueSnackbar]);

  const handleEditClick = (e: any) => {
    if (user && user.role === 'admin') {
      setIsEditing(true);
      
      setTimeout(() => {
        if (quillRef.current) {
          const quillEditor = quillRef.current.getEditor();
          quillEditor.focus();
          const clickX = e.clientX - e.target.getBoundingClientRect().left;
          const approximateIndex = Math.floor((clickX / e.target.clientWidth) * data.length);
          quillEditor.setSelection(approximateIndex, 0);
        }
      }, 0);
    }
  };

  const handleBlur = () => {
    // Add a small delay to allow paste operations to complete
    setTimeout(() => {
      setIsEditing(false);
      editMetric.mutate(
        {
          [isVolet ? "titleV" : "title"]: data.toString(),
          id,
        },
        {
          onSuccess: (res) => {
            enqueueSnackbar('mis à jour avec succès.');
          },
          onError: (err) => {
            enqueueSnackbar("quelque chose s'est mal passé", {
              variant: 'error',
            });
          },
        }
      );
    }, 200);
  };

  const handleCopyText = () => {
    const textToCopy = stripHtmlTags(data);
    navigator.clipboard.writeText(textToCopy).then(() => {
      enqueueSnackbar('Texte copié avec succès', { variant: 'success' });
    }).catch(err => {
      enqueueSnackbar('Échec de la copie du texte', { variant: 'error' });
    });
  };

  return (
    <div className="w-full h-full">
      {isEditing && user && user.role === 'admin' ? (
        <ReactQuill
          ref={quillRef}
          value={data}
          onChange={setData}
          onBlur={handleBlur}
          modules={modules}
          formats={formats}
          className="w-full"
        />
      ) : (
        <Tooltip title={stripHtmlTags(data)}>
          <div 
            onClick={handleEditClick} 
            className="w-full h-full min-h-6 cursor-pointer flex items-center justify-between"
          >
            <div 
              dangerouslySetInnerHTML={{ __html: data }} 
              className="flex-grow"
            />
            
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default TitleMetric;
